import { Box } from '@mui/material'
import Button from 'components/buttons/Button'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import PopupSection from 'components/popup/PopupSection'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { useProgress } from 'components/providers/ProgressProvider'
import { t } from 'i18next'
import { useState } from 'react'
import {
  hardDeleteGroupFromServer,
  resetConvertedGroupState,
  updateGroupPersonalSyncStatus,
  updateLinkOnServer
} from 'services/api/group'
import Database from 'services/db/Database'
import { RemoveEventsForGroup } from 'services/db/Queries/Event'
import { isOwnerForGroup } from 'services/db/utils/GroupUtils'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { GROUP_INFO_ACTION_TYPES } from 'types/api/group'
import { GroupDoc, GroupStatus, UserSettingsDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { getOrUpdateDeviceId } from 'utils/device'
import { isTestEnv } from 'utils/env'
import { isUserActiveInGroup, isUserLastActiveInGroup } from 'utils/api/groups'
import { isGroupArchived, isGroupDefault } from 'utils/api/userSettings'
import { isTrulyEmpty } from 'utils/object'
import {
  changeGroupStatus,
  deleteGroupForUser,
  groupToProvider,
  isConvertedGroup,
  PersonalGroupSyncStatus,
  ProviderType,
  toggleArchiveGroup
} from 'utils/groups'

import { parseErrorToMessage } from '../eventInfo/EventPopup'
import { ExitGroupButton } from './ExitGroupButton'
import { performUpdateGroupSettings } from './SettingsForCalendarSection'
import { APP_COLOR } from 'utils/colors'

interface CalendarManagementSectionProps {
  group: Model<GroupDoc>
  userSettings: Model<UserSettingsDoc>
  setGroupOpen: (b: boolean) => void
}

function unlinkTitle(group: Model<GroupDoc>): string {
  const provider = groupToProvider(group)

  if (provider === ProviderType.GOOGLE) {
    return t('removeGoogleCalendar')
  }

  if (provider === ProviderType.OUTLOOK) {
    return t('removeOutlookCalendar')
  }

  if (provider === ProviderType.APPLE) {
    return t('removeAppleCalendar')
  }

  return ''
}

function dialogTitle(group: Model<GroupDoc>): string {
  const provider = groupToProvider(group)

  if (provider === ProviderType.GOOGLE) {
    return t('areYouSureDeleteGoogle')
  }

  if (provider === ProviderType.OUTLOOK) {
    return t('areYouSureDeleteOutlook')
  }

  if (provider === ProviderType.APPLE) {
    return t('areYouSureDeleteApple')
  }

  return ''
}

function DebugSection(props: CalendarManagementSectionProps) {
  const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false)

  if (!isTestEnv()) {
    return <></>
  }

  return (
    <Box display={'flex'} flexDirection={'column'}>
      <PopupSection title="DEBUG OPTIONS" simplified>
        <PopupSectionRow
          onlyTitle
          actionId="debug-delete-group"
          title={'Delete group from server'}
          value={<Button size="xs">{t('delete')}</Button>}
          action={() => {
            setShowDeleteGroupDialog(true)
          }}
        />
      </PopupSection>
      {showDeleteGroupDialog && (
        <ConfirmationDialog
          title={'This action removes group from a server completely'}
          content={''}
          open={true}
          handleLeftButton={() => {
            setShowDeleteGroupDialog(false)
          }}
          handleRightButton={() => {
            hardDeleteGroupFromServer(props.group._id).then(() => {
              setShowDeleteGroupDialog(false)

              RemoveEventsForGroup(props.group._id).then(() => {
                return Database.write(async () => {
                  return props.group.markAsDeleted()
                })
              })
            })
          }}
        />
      )}
    </Box>
  )
}

export default function CalendarManagementSection(
  props: CalendarManagementSectionProps
) {
  const calendarSync = useCalendarSync()
  const calendar = useCalendar()
  const { setError, setLoading } = useProgress()
  const [showLeaveAndDeleteExplanation, setShowLeaveAndDeleteExplanation] =
    useState(false)
  const [showDeleteExplanation, setShowDeleteExplanation] = useState(false)

  const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  const isCurrentGroupThirdParty = isConvertedGroup(props.group)

  const removeGoogleCalendar = () => {
    calendarSync
      .logout(props.group)
      .then(() => {
        props.setGroupOpen(false)
        calendar.onGroupServer('')
      })
      .finally(() => {
        if (isGroupDefault(props.userSettings, props.group._id)) {
          performUpdateGroupSettings(props.userSettings, props.group._id, {
            Default: '0',
            GroupStatus: GroupStatus.ARCHIVED
          })
        }

        resetConvertedGroupState(props.group)
        if (
          props.group.PersonalGroupSyncStatus === PersonalGroupSyncStatus.SYNCED
        ) {
          updateGroupPersonalSyncStatus('', props.group)
        }
        if (
          !isTrulyEmpty(props.group.PrivateLinkPassword) &&
          !isTrulyEmpty(props.group.PrivateLinkUrl)
        ) {
          updateLinkOnServer(
            {
              PrivateLinkUrl: '',
              PrivateLinkPassword: '',
              ActionType: GROUP_INFO_ACTION_TYPES.METADATA,
              UserID: userId,
              GroupID: props.group._id,
              DeviceChangeID: getOrUpdateDeviceId()
            },
            props.group
          )
        }
      })
  }

  const performGroupDelete = async () => {
    setShowLeaveAndDeleteExplanation(false)
    setLoading(true)

    try {
      await deleteGroupForUser(props.group)
    } catch (e) {
      Logger.red(String(e))
      setError(parseErrorToMessage(e))
    }

    props.setGroupOpen(false)
    calendar.onGroupServer('')
    LocalStorage.remove(LocalStorageKey.SHOWING_GROUP_INFO)
    setLoading(false)
  }

  const isUserActive = isUserActiveInGroup(props.group, userId)
  const isLastActive = isUserLastActiveInGroup(props.group, userId)
  const isArchived = isGroupArchived(props.userSettings, props.group._id)

  return (
    <>
      {((isCurrentGroupThirdParty && props.group.OwnerID === userId) ||
        !isCurrentGroupThirdParty) && (
        <PopupSection
          title={t('sharingAndMembershipManagement')}
          popupSectionGap={'8px'}
          simplified
        >
          <PopupSectionRow
            onlyTitle
            title={t(isArchived ? 'unarchiveCalendar' : 'archiveCalendar')}
            value={
              <Button size="xs">
                <div style={{ color: 'red' }}>
                  {t(isArchived ? 'unarchive' : 'archive')}
                </div>
              </Button>
            }
            action={() => {
              toggleArchiveGroup(props.group, props.userSettings).then(() => {
                calendar.onGroupServer('')
              })
            }}
          />
          {isCurrentGroupThirdParty && props.group.OwnerID === userId ? (
            <ExitGroupButton
              group={props.group}
              setLoading={setLoading}
              rowTitle={unlinkTitle(props.group)}
              dialogTitle={dialogTitle(props.group)}
              positiveTitle={`${t('unlink')}`}
              action={removeGoogleCalendar}
            />
          ) : isUserActive && !isLastActive ? (
            <ExitGroupButton group={props.group} setLoading={setLoading} />
          ) : null}
          {isUserActive && (
            <PopupSectionRow
              onlyTitle
              title={t('leaveAnddeleteCalendar')}
              explanation={t('deleteCalendarDesc')}
              value={
                <Button size="xs">
                  <div style={{ color: 'red' }}>{t('leaveAnddelete')}</div>
                </Button>
              }
              action={() => {
                setShowLeaveAndDeleteExplanation(true)
              }}
            />
          )}
          {!isUserActive && (
            <PopupSectionRow
              onlyTitle
              explanation={t('deleteCalendarExplanation')}
              title={t('deleteCalendar')}
              value={
                <Button size="xs">
                  <div style={{ color: 'red' }}>{t('delete')}</div>
                </Button>
              }
              action={() => {
                setShowDeleteExplanation(true)
              }}
            />
          )}
        </PopupSection>
      )}

      {showDeleteExplanation && (
        <ConfirmationDialog
          title={t('areYouSure')}
          content={t('deleteCalendarExplanation')}
          leftButtonColor="grey"
          rightButtonColor={APP_COLOR}
          rightButton={t('delete')}
          open
          handleRightButton={() => {
            changeGroupStatus(props.group, GroupStatus.DELETED).then(() => {
              props.setGroupOpen(false)
              calendar.onGroupServer('')
              calendar.onGroupInfo(undefined)
              setLoading(false)
            })
          }}
          handleLeftButton={() => {
            setShowDeleteExplanation(false)
          }}
          handleClose={() => {
            setShowDeleteExplanation(false)
          }}
        />
      )}
      {showLeaveAndDeleteExplanation && (
        <ConfirmationDialog
          title={t('areYouSure')}
          content={t('deleteCalendarDesc')}
          leftButtonColor="grey"
          rightButtonColor={APP_COLOR}
          rightButton={t('leaveAnddelete')}
          open
          handleRightButton={performGroupDelete}
          handleLeftButton={() => {
            setShowLeaveAndDeleteExplanation(false)
          }}
          handleClose={() => {
            setShowLeaveAndDeleteExplanation(false)
          }}
        />
      )}

      <DebugSection {...props} />
    </>
  )
}
